import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { CustomEvent } from "schemas/functions";

import { fetcherAuth } from "utils/api";

import { MapListItem } from "../MapListItem";
import { GooglePlaceBrand, GooglePlaceResult } from "../schema";
import { convertPlace } from "./utils";

interface IProps {
  addedPlaces: GooglePlaceResult[];
  addPlace: (place: GooglePlaceResult) => void;
  open: boolean;
  handleClose: () => void;
  openBrandVerificationDialog: () => void;
  setGooglePlaceBrands: Dispatch<SetStateAction<GooglePlaceBrand[]>>;
}

export default function CheckoutDialog({
  addedPlaces,
  addPlace,
  open,
  handleClose,
  openBrandVerificationDialog,
  setGooglePlaceBrands,
}: IProps) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const [nextLoading, setNextLoading] = useState<boolean>(false);

  const continuePressed = async (e: CustomEvent) => {
    e.preventDefault();

    if (!currentOrg?.id) {
      return;
    }

    if (addedPlaces.length === 0) {
      setAlert("You need to have at least one brand selected", "error");
      return;
    }

    await handleCreateBrands();
  };

  const handleCreateBrands = async () => {
    if (!currentOrg?.id) return;

    setNextLoading(true);
    try {
      const { googlePlaceBrands } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/google-place-outreach-collections/places`,
        "POST",
        {},
        {
          places: addedPlaces.map((place) => convertPlace(place)),
        },
      );

      setGooglePlaceBrands(googlePlaceBrands);
      openBrandVerificationDialog();
      handleClose();
    } catch (error) {
      setAlert(
        error?.message ||
          "Something went wrong, please contact hello@onbento.com for support or try again",
        "error",
      );
    } finally {
      setNextLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-us">
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        fullScreen={isMobileScreen}
        maxWidth="md"
      >
        <DialogTitle>
          <Typography variant="h4">
            Finalize Brands ({addedPlaces.length})
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText paragraph>
            All brands on this list will be sent using the same template. Please
            review and finalize the brands you'd like to include.
          </DialogContentText>
          <Box>
            <Typography gutterBottom variant="h6"></Typography>
            <Box
              sx={{
                overflowY: "auto",
                maxHeight: { xs: "none", md: "70vh" },
              }}
            >
              {addedPlaces.map((place) => (
                <Box key={place.id} sx={{ pr: 2 }}>
                  <MapListItem
                    place={place}
                    isSelected={false}
                    addPlace={addPlace}
                    isAdded={addedPlaces.some((p) => p.id === place.id)}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={handleClose}>
            Close
          </Button>
          <LoadingButton
            loading={nextLoading}
            color="primary"
            variant="contained"
            disableElevation
            onClick={continuePressed}
          >
            Continue
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
